import { db } from '../firebase/config';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

/**
 * Get coaching name from coachingId
 * @param {string} coachingId - The coaching ID
 * @returns {Promise<string>} - The coaching name (first 3 characters)
 */
export const getCoachingNameAbbrev = async (coachingId) => {
  try {
    // First, try to get from coachingCenters collection
    try {
      const coachingCenterDoc = await getDoc(doc(db, 'coachingCenters', coachingId));
      if (coachingCenterDoc.exists()) {
        const centerData = coachingCenterDoc.data();
        const schoolName =
          centerData.name ||
          centerData.schoolName ||
          centerData.instituteName ||
          centerData.coaching_name ||
          null;
        
        if (schoolName) {
          console.log(`Found coaching name in coachingCenters: ${schoolName}`);
          return schoolName.substring(0, 3).toUpperCase();
        }
      }
    } catch (error) {
      console.log('coachingCenters lookup failed, trying users collection:', error.message);
    }

    // Fallback: Try to get coaching name from the coaching admin user profile
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('coachingId', '==', coachingId),
      where('role', '==', 'coaching_admin')
    );
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const userData = snapshot.docs[0].data();
      // Try different field names for coaching/institute name
      const schoolName =
        userData.coachingName ||
        userData.coaching_name ||
        userData.instituteName ||
        userData.institute_name ||
        userData.name ||
        null;

      if (schoolName) {
        console.log(`Found coaching name in users collection: ${schoolName}`);
        return schoolName.substring(0, 3).toUpperCase();
      }
    }

    // Second fallback: Try to get from any user with this coachingId
    const anyUserQuery = query(usersRef, where('coachingId', '==', coachingId));
    const anyUserSnapshot = await getDocs(anyUserQuery);
    
    if (!anyUserSnapshot.empty) {
      const userData = anyUserSnapshot.docs[0].data();
      const schoolName = userData.coachingName || userData.name || null;
      
      if (schoolName) {
        console.log(`Found coaching name from any user: ${schoolName}`);
        return schoolName.substring(0, 3).toUpperCase();
      }
    }

    console.warn(`No coaching name found for coachingId: ${coachingId}`);
    return 'SCH'; // Default if not found (School)
  } catch (error) {
    console.error('Error getting coaching name:', error);
    return 'SCH'; // Default fallback
  }
};

/**
 * Get the next sequential number for a school (accounting for existing numbers)
 * @param {string} coachingId - The coaching ID
 * @returns {Promise<number>} - The next 5-digit number (as number: 1, 2, 3, etc.)
 */
export const getNextStudentNumber = async (coachingId) => {
  try {
    // Query all students for this coaching
    const studentsRef = collection(db, 'students');
    const q = query(studentsRef, where('coachingId', '==', coachingId));
    const snapshot = await getDocs(q);

    // Find the highest existing number
    let maxNumber = 0;
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.registrationNumber) {
        // Extract the 5-digit number from the end of registration number
        const regNum = data.registrationNumber;
        const lastFiveDigits = regNum.slice(-5);
        const numValue = parseInt(lastFiveDigits, 10);
        if (!isNaN(numValue) && numValue > maxNumber) {
          maxNumber = numValue;
        }
      }
    });

    // Next number should be max + 1
    const nextNumber = maxNumber + 1;
    console.log(`getNextStudentNumber for ${coachingId}: maxNumber=${maxNumber}, nextNumber=${nextNumber}`);
    return nextNumber;
  } catch (error) {
    console.error('Error getting next student number:', error);
    return 1;
  }
};

/**
 * Format number to 5 digits with leading zeros
 * @param {number} num - The number to format
 * @returns {string} - The formatted 5-digit number (e.g., "00001")
 */
const formatNumber = (num) => {
  return String(num).padStart(5, '0');
};

/**
 * Get first 3 characters from a name
 * @param {string} name - The name to abbreviate
 * @returns {string} - First 3 characters in uppercase
 */
const getNameAbbrev = (name) => {
  return (name || 'STU').substring(0, 3).toUpperCase();
};

/**
 * Generate registration number for a student with proper sequential numbering
 * Format: MAHSTRY(YYYY)XXXYYY00001
 * Where:
 * - MAHSTRY = Fixed prefix
 * - YYYY = Current year
 * - XXX = First 3 characters of school name
 * - YYY = First 3 characters of student first name
 * - 00001 = 5-digit sequential number (unique per school)
 *
 * @param {object} params - Parameters object
 * @param {string} params.coachingId - The coaching ID
 * @param {string} params.studentName - The student's first name
 * @returns {Promise<string>} - The generated registration number
 */
export const generateRegistrationNumber = async ({ coachingId, studentName }) => {
  try {
    // Get current year
    const currentYear = new Date().getFullYear();

    // Get coaching name abbreviation
    const schoolAbbrev = await getCoachingNameAbbrev(coachingId);

    // Get student name abbreviation
    const studentAbbrev = getNameAbbrev(studentName);

    // Get next sequential number for this school
    const nextNumber = await getNextStudentNumber(coachingId);
    const formattedNumber = formatNumber(nextNumber);

    // Generate registration number
    const registrationNumber = `MAHSTRY${currentYear}${schoolAbbrev}${studentAbbrev}${formattedNumber}`;

    console.log(`Generated registration number: ${registrationNumber} (next number: ${nextNumber})`);
    return registrationNumber;
  } catch (error) {
    console.error('Error generating registration number:', error);
    // Return a safe default with current year and student name
    const currentYear = new Date().getFullYear();
    const studentAbbrev = getNameAbbrev(studentName);
    return `MAHSTRY${currentYear}SCH${studentAbbrev}${formatNumber(1)}`;
  }
};
